import { API } from '../lib/api';
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  BookOpen, 
  Wand2, 
  Plus, 
  User, 
  X, 
  FileText, 
  Key, 
  Sparkles 
} from 'lucide-react';

export const ProjectPage: React.FC = () => {
  return (
    <div className="main-content">
      <h1 className="text-4xl font-elegant text-gradient mb-8">Édition de projet</h1>
      <div className="card">
        <p className="font-children text-gray-600">
          Interface d'édition de projet - À implémenter à l'étape 3
        </p>
      </div>
    </div>
  );
};

export const CreateProjectPage: React.FC = () => {
  const [step, setStep] = React.useState(1);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    title: '',
    textContent: '',
    globalStyle: 'Style magique et coloré, illustrations douces pour enfants de 3-6 ans',
    characters: [] as { name: string; description: string }[],
    openaiApiKey: ''
  });

  const addCharacter = () => {
    setFormData({
      ...formData,
      characters: [...formData.characters, { name: '', description: '' }]
    });
  };

  const updateCharacter = (index: number, field: string, value: string) => {
    const updatedCharacters = [...formData.characters];
    updatedCharacters[index] = { ...updatedCharacters[index], [field]: value };
    setFormData({ ...formData, characters: updatedCharacters });
  };

  const removeCharacter = (index: number) => {
    const updatedCharacters = formData.characters.filter((_, i) => i !== index);
    setFormData({ ...formData, characters: updatedCharacters });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Validation côté client
      if (!formData.title.trim()) {
        throw new Error('Le titre est requis');
      }
      if (!formData.textContent.trim()) {
        throw new Error('Le contenu textuel est requis');
      }
      if (formData.textContent.length < 50) {
        throw new Error('Le contenu doit contenir au moins 50 caractères');
      }

      // Compter les pages (paragraphes)
      const pageCount = formData.textContent
        .split(/\n\s*\n/)
        .filter(p => p.trim().length > 0).length;
      
      if (pageCount === 0) {
        throw new Error('Aucune page détectée. Séparez votre texte en paragraphes.');
      }

      console.log(`🚀 Envoi de ${pageCount} pages pour génération...`);

      // Appel à l'API
      const response = await fetch(`${API}/api/projects/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          globalStyle: formData.globalStyle.trim(),
          characters: formData.characters.filter(char => 
            char.name.trim() && char.description.trim()
          ),
          textContent: formData.textContent.trim(),
          openaiApiKey: formData.openaiApiKey.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du projet');
      }

      const result = await response.json();
      
      setSuccess(
        `🎉 Génération démarrée avec succès ! \n` +
        `Votre livre "${formData.title}" sera prêt dans ${result.estimatedTime}. \n` +
        `Vous pouvez suivre le progrès dans le tableau de bord.`
      );

      // Rediriger vers le dashboard après un délai
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 3000);

    } catch (error) {
      console.error('Erreur création projet:', error);
      setError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="main-content max-w-4xl mx-auto">
      <div className="flex items-center mb-8">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 mr-4">
          <ArrowLeft className="h-6 w-6" />
        </Link>
        <div>
          <h1 className="text-4xl font-elegant text-gradient mb-2">
            Nouveau Livre Illustré
          </h1>
          <p className="text-gray-600 font-children">
            Créez votre histoire et définissez vos personnages
          </p>
        </div>
      </div>

      {/* Messages d'erreur et de succès */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="h-5 w-5 text-red-500 mr-3">❌</div>
            <div>
              <h3 className="text-red-800 font-children font-semibold">Erreur</h3>
              <p className="text-red-700 font-children text-sm mt-1 whitespace-pre-line">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="h-5 w-5 text-green-500 mr-3">✅</div>
            <div>
              <h3 className="text-green-800 font-children font-semibold">Succès</h3>
              <p className="text-green-700 font-children text-sm mt-1 whitespace-pre-line">{success}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Étape 1: Informations de base */}
        <div className="card">
          <div className="flex items-center mb-6">
            <div className="h-8 w-8 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
              1
            </div>
            <h2 className="text-xl font-elegant font-semibold">
              Informations du livre
            </h2>
          </div>

          <div className="space-y-6">
            <div>
              <label className="label">
                <BookOpen className="h-4 w-4 inline mr-2" />
                Titre du livre
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="Ex: Les aventures de Max le petit dragon"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">
                <Wand2 className="h-4 w-4 inline mr-2" />
                Style artistique
              </label>
              <textarea
                className="textarea-field"
                placeholder="Décrivez le style artistique souhaité..."
                value={formData.globalStyle}
                onChange={(e) => setFormData({ ...formData, globalStyle: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Étape 2: Personnages */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                2
              </div>
              <h2 className="text-xl font-elegant font-semibold">
                Personnages principaux
              </h2>
            </div>
            <button
              type="button"
              onClick={addCharacter}
              className="btn-secondary inline-flex items-center space-x-2 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter</span>
            </button>
          </div>

          {formData.characters.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-children">
                Ajoutez vos personnages pour maintenir la cohérence visuelle
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.characters.map((character, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-children font-semibold text-gray-700">
                      Personnage {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeCharacter(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="label text-sm">Nom</label>
                      <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="Ex: Max"
                        value={character.name}
                        onChange={(e) => updateCharacter(index, 'name', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="label text-sm">Description</label>
                      <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="Ex: Petit dragon vert avec des ailes dorées"
                        value={character.description}
                        onChange={(e) => updateCharacter(index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Étape 3: Histoire */}
        <div className="card">
          <div className="flex items-center mb-6">
            <div className="h-8 w-8 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
              3
            </div>
            <h2 className="text-xl font-elegant font-semibold">
              Votre histoire
            </h2>
          </div>

          <div>
            <label className="label">
              <FileText className="h-4 w-4 inline mr-2" />
              Texte du livre (chaque paragraphe = une page)
            </label>
            <textarea
              className="textarea-field"
              placeholder="Collez votre histoire ici...\n\nChaque paragraphe séparé par une ligne vide deviendra une page du livre.\n\nPar exemple :\nIl était une fois...\n\nLe petit héros décida...\n\nEt ils vécurent heureux..."
              value={formData.textContent}
              onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
              rows={12}
              required
            />
            <p className="text-sm text-gray-500 mt-2 font-children">
              Conseils : Séparez chaque page par une ligne vide. Chaque paragraphe deviendra une page illustrée.
            </p>
          </div>
        </div>

        {/* Étape 4: Configuration IA */}
        <div className="card bg-gradient-to-br from-purple-50 to-pink-50">
          <div className="flex items-center mb-6">
            <div className="h-8 w-8 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
              4
            </div>
            <h2 className="text-xl font-elegant font-semibold">
              Configuration IA
            </h2>
          </div>

          <div>
            <label className="label">
              <Key className="h-4 w-4 inline mr-2" />
              Clé API OpenAI (optionnel)
            </label>
            <input
              type="password"
              className="input-field"
              placeholder="sk-..."
              value={formData.openaiApiKey}
              onChange={(e) => setFormData({ ...formData, openaiApiKey: e.target.value })}
            />
            <p className="text-sm text-gray-500 mt-2 font-children">
              Si vide, utilisera la clé par défaut (limitée). Pour un usage intensif, ajoutez votre clé OpenAI.
            </p>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Link 
            to="/dashboard"
            className="text-gray-500 hover:text-gray-700 font-children"
          >
            Annuler
          </Link>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`btn-magical inline-flex items-center space-x-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Génération en cours...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                <span>Créer le livre</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export const NotFoundPage: React.FC = () => {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-6xl font-elegant text-gradient mb-4">404</h1>
        <p className="text-xl font-children text-gray-600 mb-8">
          Cette page n'existe pas dans notre livre magique !
        </p>
        <a href="/" className="btn-magical">
          Retour à l'accueil
        </a>
      </div>
    </div>
  );
};
